import React from 'react';
import { COLORS } from './palette';
import { Sparkline } from './charts';

// ---------------------------------------------------------------------------
// Panel — the card shell every chart/section lives in. Ported from the
// custom-analytics `Panel` (minus the SQL viewer, which is inspo-specific) and
// recolored to Livable's light theme. `span` is a 1–12 grid column span.
// ---------------------------------------------------------------------------
export const Panel = _ref => {
  let title = _ref.title,
    subtitle = _ref.subtitle,
    _ref$span = _ref.span,
    span = _ref$span === void 0 ? 6 : _ref$span,
    _ref$accent = _ref.accent,
    accent = _ref$accent === void 0 ? COLORS.teal : _ref$accent,
    right = _ref.right,
    height = _ref.height,
    children = _ref.children;
  return /*#__PURE__*/React.createElement("section", {
    style: {
      gridColumn: span === 12 ? '1 / -1' : "span ".concat(span),
      background: COLORS.panel,
      border: "1px solid ".concat(COLORS.border),
      borderRadius: 10,
      padding: '16px 18px 14px',
      boxShadow: '0 1px 2px rgba(33,41,52,0.04), 0 6px 18px rgba(33,41,52,0.05)',
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement("header", {
    style: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: 12
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      width: 4,
      height: 18,
      borderRadius: 2,
      background: accent,
      flex: '0 0 auto'
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement("h3", {
    style: {
      margin: 0,
      fontSize: 14,
      fontWeight: 600,
      color: COLORS.text,
      letterSpacing: 0.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  }, title), subtitle && /*#__PURE__*/React.createElement("p", {
    style: {
      margin: '2px 0 0',
      fontSize: 12,
      color: COLORS.textFaint
    }
  }, subtitle))), right && /*#__PURE__*/React.createElement("div", {
    style: {
      flex: '0 0 auto'
    }
  }, right)), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      minHeight: height
    }
  }, children));
};

// ---------------------------------------------------------------------------
// KpiCard — headline stat with optional sparkline and a colored accent value.
// `trend` (optional number[]) renders the sparkline; `tone` colors the value.
// ---------------------------------------------------------------------------
export const KpiCard = _ref2 => {
  let label = _ref2.label,
    value = _ref2.value,
    _ref2$tone = _ref2.tone,
    tone = _ref2$tone === void 0 ? COLORS.text : _ref2$tone,
    trend = _ref2.trend,
    trendColor = _ref2.trendColor,
    footnote = _ref2.footnote;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      background: COLORS.panel,
      border: "1px solid ".concat(COLORS.border),
      borderRadius: 10,
      padding: '14px 16px',
      boxShadow: '0 1px 2px rgba(33,41,52,0.04)',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: COLORS.textFaint,
      textTransform: 'uppercase',
      letterSpacing: 0.6
    }
  }, label), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 26,
      fontWeight: 700,
      color: tone,
      lineHeight: 1.1,
      whiteSpace: 'nowrap'
    }
  }, value), trend && /*#__PURE__*/React.createElement(Sparkline, {
    data: trend,
    color: trendColor
  })), footnote && /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      color: COLORS.textFaint
    }
  }, footnote));
};

// ---------------------------------------------------------------------------
// EmptyState — shown inside a Panel when a breakdown has no data for the
// current filter selection.
// ---------------------------------------------------------------------------
export const EmptyState = _ref3 => {
  let message = _ref3.message;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      height: '100%',
      minHeight: 220,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: COLORS.textFaint,
      fontSize: 13,
      border: "1px dashed ".concat(COLORS.border),
      borderRadius: 8
    }
  }, message);
};